import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export interface NormalizedIpDetails {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  loc?: string;
  org?: string;
  postal?: string;
  timezone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseUrl: string = environment.baseUrl;

  /** Emits after local session is cleared (login pages, interceptor 403, etc.). */
  private readonly loggedOutSubject = new Subject<void>();
  readonly loggedOut$ = this.loggedOutSubject.asObservable();

  private logoutInFlight = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private injector: Injector,
  ) { }

  login(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/signin`, payload);
  }

  logout(): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/logout`, {});
  }

  /**
   * Clear session and navigate home.
   * Best-effort FCM token DELETE runs first while Bearer is still available.
   */
  postLogout() {
    if (this.logoutInFlight) {
      return;
    }
    this.logoutInFlight = true;

    const finish = () => {
      localStorage.clear();
      sessionStorage.removeItem('FCM_WEB_TOKEN');
      this.loggedOutSubject.next();
      this.logoutInFlight = false;
      this.router.navigate(['/']);
    };

    void import('../push-notifications/push-notifications.service')
      .then(({ PushNotificationsService }) => {
        const push = this.injector.get(PushNotificationsService);
        return push.unregisterPushNotifications();
      })
      .catch(() => undefined)
      .finally(finish);
  }

  isAuthenticated() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.access_token ? true : false;
  }

  forgotPassword(
    identifier: string,
    identifierType: 'email' | 'mobile_number' = 'email',
  ): Observable<any> {
    const payload =
      identifierType === 'mobile_number'
        ? { mobile_number: identifier, type: 'forgot_password' }
        : { email: identifier, type: 'forgot_password' };
    return this.http.put(`${this.baseUrl}/auth/forgot-password`, payload);
  }

  verifyOtp(
    identifier: string,
    code: string,
    _identifierType: 'email' | 'mobile_number' = 'email',
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/auth/verify-otp`, {
      email: identifier,
      code,
      type: 'forgot_password',
    });
  }

  resetPassword(
    identifier: string,
    password: string,
    confirmPassword: string,
    _identifierType: 'email' | 'mobile_number' = 'email',
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/auth/reset-password`, {
      email: identifier,
      password,
      confirm_password: confirmPassword,
    });
  }

  /**
   * Fetches IP details from backend (GET /auth/ip-info).
   * Backend calls ipwhois.pro internally.
   */
  getIpDetailsFromServer(): Observable<NormalizedIpDetails> {
    return this.http.get<any>(`${this.baseUrl}/auth/ip-info`).pipe(
      map((res) => {
        const data = res.data ?? res;
        return {
          ip: data?.ip,
          city: data?.city,
          region: data?.region,
          country: data?.country_code ?? data?.country,
          loc: data?.latitude != null && data?.longitude != null
            ? `${data.latitude},${data.longitude}`
            : undefined,
          org: data?.connection?.org ?? data?.org,
          postal: data?.postal,
          timezone: data?.timezone?.id ?? data?.timezone,
        };
      }),
    );
  }

  /**
   * Legacy: fetches IP from ipwho.is directly (used as fallback).
   */
  getIpDetails(): Observable<any> {
    return this.http.get<any>('https://ipwho.is/');
  }

  /**
   * Gets IP details with fallback: tries backend first, falls back to ipwho.is.
   * Preserves existing flow when backend ip-info is unavailable.
   */
  getIpDetailsWithFallback(): Observable<NormalizedIpDetails> {
    return this.getIpDetailsFromServer().pipe(
      catchError(() =>
        this.getIpDetails().pipe(
          map((data) => this.normalizeIpWhoIs(data)),
        ),
      ),
    );
  }

  private normalizeIpWhoIs(data: any): NormalizedIpDetails {
    return {
      ip: data?.ip,
      city: data?.city,
      region: data?.region,
      country: data?.country_code ?? data?.country,
      loc:
        data?.latitude != null && data?.longitude != null
          ? `${data.latitude},${data.longitude}`
          : undefined,
      org: data?.connection?.org ?? data?.org,
      postal: data?.postal,
      timezone: data?.timezone?.id ?? data?.timezone ?? data?.timezone_id,
    };
  }

  sendRegistrationOtp(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/signup/send-otp`, payload);
  }

  verifyRegistrationOtp(payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/auth/signup/verify-otp`, payload);
  }

  submitRegistration(payload: {
    username: any,
    email: any,
    mobileNumber: any,
    password: any,
    selectedFiles: any
  }): Observable<any> {
    const formData = new FormData();
    formData.append('username', payload.username);
    formData.append('email', payload.email);
    formData.append('mobile_number', payload.mobileNumber);
    formData.append('password', payload.password);
    if (payload.selectedFiles && payload.selectedFiles.length > 0) {
      for (const file of payload.selectedFiles) {
        formData.append('documents', file, file.name);
      }
    }
    return this.http.post(`${this.baseUrl}/auth/signup/submit`, formData);
  }

  verify2Fa(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/2fa/verify-login`, payload);
  }


}
